import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";

export function invalidateInboxItems() {
  return queryClient.invalidateQueries({ queryKey: ["inbox-items"] });
}

export interface OwnPendingMemory {
  id: string;
  memory_type: string;
  created_at: string;
  audio_url: string | null;
  image_url: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  audio_duration_seconds: number | null;
  video_duration_seconds: number | null;
  title: string | null;
}

export interface ReviewMemory {
  id: string;
  title: string | null;
  raw_transcript: string | null;
  ai_chapter: string | null;
  memory_type: string;
  created_at: string;
  user_id: string;
  contributor_name: string | null;
}

export type InboxItem =
  | { kind: "own"; data: OwnPendingMemory }
  | { kind: "review"; data: ReviewMemory };

export function useInboxItems(open: boolean) {
  const query = useQuery({
    queryKey: ["inbox-items"],
    enabled: open,
    staleTime: 0,
    queryFn: async () => {
      const [ownRes, reviewRes] = await Promise.all([
        supabase
          .from("memories")
          .select(
            "id, memory_type, created_at, audio_url, image_url, video_url, thumbnail_url, audio_duration_seconds, video_duration_seconds, title",
          )
          .eq("status", "pending")
          .order("created_at", { ascending: false }),
        supabase
          .from("memories")
          .select(
            "id, title, raw_transcript, ai_chapter, memory_type, created_at, user_id, contributor_name",
          )
          .eq("approval_status", "pending")
          .order("created_at", { ascending: false }),
      ]);

      const own: InboxItem[] = (ownRes.data ?? []).map((d) => ({
        kind: "own" as const,
        data: d as OwnPendingMemory,
      }));
      const review: InboxItem[] = (reviewRes.data ?? []).map((d) => ({
        kind: "review" as const,
        data: d as ReviewMemory,
      }));
      return [...own, ...review];
    },
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
